import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import ArticleCard from "./components/ArticleCard";
import Sidebar from "./components/Sidebar";
import ArticlePage from "./components/ArticlePage";
import CategoryPage from "./components/CategoryPage";
import WriteArticle from "./components/WriteArticle";
import ScheduledArticles from "./components/ScheduledArticles";
import Statistics from "./components/Statistics";
import SearchResults from "./components/SearchResults";
import Contributors from "./components/Contributors";
import Login from "./components/Login";
import Footer from "./components/Footer";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { fetchArticles, fetchActiveCategories } from "./services/api";
import { Loader2, PenLine } from "lucide-react";
import { Link } from "react-router-dom";

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const [data, cats] = await Promise.all([
        fetchArticles({ limit: 50 }),
        fetchActiveCategories(),
      ]);
      setArticles(data.articles || []);
      setActiveCategories(cats || []);
    } catch (err) {
      console.error("Failed to load articles:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadArticles();
    // Auto-refresh every 30 minutes
    const interval = setInterval(loadArticles, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadArticles]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1B7A3D] mx-auto mb-3" size={32} />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const featured = articles.find((a) => a.featured);
  const latestArticles = articles.filter((a) => !a.featured).slice(0, 10);
  const mainArticles = latestArticles.slice(0, 6);
  const moreArticles = latestArticles.slice(6);

  // Empty state
  if (articles.length === 0) {
    return (
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <div className="bg-white rounded-2xl border border-gray-100 p-12">
          <PenLine className="mx-auto text-[#1B7A3D] mb-4" size={48} />
          <h2 className="text-2xl font-black text-gray-900 mb-2">No Articles Yet</h2>
          <p className="text-gray-500 mb-6">
            Be the first to share your sports opinion on On The Counter.
          </p>
          <Link
            to="/write"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B7A3D] hover:bg-[#15612F] text-white font-semibold rounded-lg transition-colors"
          >
            <PenLine size={18} />
            Write Your First Article
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <HeroSection featured={featured} categories={activeCategories} />

      {/* Latest Articles Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-1 h-6 bg-[#1B7A3D] rounded-full" />
              <h2 className="text-xl font-bold text-gray-900">Latest Opinion</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {mainArticles.slice(0, 4).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Horizontal articles */}
            <div className="space-y-2">
              {mainArticles.slice(4).map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant="horizontal"
                />
              ))}
            </div>

            {/* Category Quick Links */}
            {/* More Articles */}
            {moreArticles.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1 h-6 bg-[#1B7A3D] rounded-full" />
                  <h2 className="text-xl font-bold text-gray-900">More Stories</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {moreArticles.slice(0, 4).map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Sidebar categories={activeCategories} showOnThisDay />
          </div>
        </div>
      </section>
    </>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="animate-spin text-[#1B7A3D]" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App min-h-screen bg-gray-50">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/write" element={<ProtectedRoute><WriteArticle /></ProtectedRoute>} />
            <Route path="/edit/:id" element={<ProtectedRoute><WriteArticle /></ProtectedRoute>} />
            <Route path="/scheduled" element={<ProtectedRoute><ScheduledArticles /></ProtectedRoute>} />
            <Route path="/statistics" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
            <Route path="/contributors" element={<ProtectedRoute><Contributors /></ProtectedRoute>} />
          </Routes>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
